import React, { useEffect, useState, ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../main/initializers/axios';
import './Company.css';
import { useTranslation } from 'react-i18next';
import { compressImageIfNeeded, DEFAULT_IMAGE_MAX_KB } from '../../main/utils/imageCompression';
import { downloadCompanyQrTemplate } from '../../main/utils/companyQrTemplate';
import { ApiConstants } from '../../main/constants/ApiConstants';
import LocationPickerModal, { type LocationPickerValue } from '../../main/components/locationPicker/LocationPickerModal';
import { buildEntityFileUrl } from '../../main/utils/fileUrl';

export interface Company {
  id: number;
  name: string;
  code: string;
  location?: {
    id?: number | null;
    name?: string | null;
    type?: string | null;
    lat?: number | null;
    lng?: number | null;
  } | null;
  locationId?: number | null;
  locationName: string;
  logoUrl: string;
  description: string;
  website: string;
  menyKeyCode: string;
  licenseKey: string;
  licenseUrl: string;
  registrationNumber: string;
  currencyId?: number | null;
  integrationId?: number | null;
  languageId?: number | null;
  isActive: boolean;
  isPublic: boolean;

  isVatRegistered: boolean;
  defaultVatRate?: string; // UI keeps it as string (e.g. "20" or "0")

  email: string;
  phone: string;
  fax: string;
  workingHours: string;
}

interface CurrencyOption {
  id: number;
  name: string;
  code: string;
  symbol?: string | null;
  isActive: boolean;
}

interface CompanyIntegrationSettingOption {
  integrationId: number;
  integrationCode: string;
  integrationName: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
}

const CompanyModal: React.FC<Props> = ({ show, onClose }) => {
  const maxUploadBytes = DEFAULT_IMAGE_MAX_KB * 1024;
  const [company, setCompany] = useState<Company | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [licensePreview, setLicensePreview] = useState<string>('');
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [integrationOptions, setIntegrationOptions] = useState<CompanyIntegrationSettingOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadingQr, setIsDownloadingQr] = useState<boolean>(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);

  const { i18n } = useTranslation();

  const languageOptions = [
    { id: 1, label: 'Shqip' },
    { id: 2, label: 'English' },
    { id: 3, label: 'Italiano' },
  ];

  const fetchComp = () => {
    setIsLoading(true);

    axiosInstance
      .get<Company>('/api/company')
      .then((res) => {
        const companyData = res.data;

        const processedData: Company = {
          ...companyData,
          phone: companyData.phone === 'null' || !companyData.phone ? '' : companyData.phone,
          fax: companyData.fax === 'null' || !companyData.fax ? '' : companyData.fax,
          workingHours: companyData.workingHours === 'null' || !companyData.workingHours ? '' : companyData.workingHours,
          locationName: companyData.locationName || companyData.location?.name || '',
          locationId: companyData.locationId ?? companyData.location?.id ?? null,
          menyKeyCode: companyData.menyKeyCode === 'null' || !companyData.menyKeyCode ? '' : companyData.menyKeyCode,
          licenseKey: companyData.licenseKey === 'null' || !companyData.licenseKey ? '' : companyData.licenseKey,
          licenseUrl: companyData.licenseUrl === 'null' || !companyData.licenseUrl ? '' : companyData.licenseUrl,
          currencyId: companyData.currencyId ?? null,
          integrationId: companyData.integrationId ?? null,
          languageId: companyData.languageId ?? null,
          isPublic: !!companyData.isPublic,
          isVatRegistered: !!companyData.isVatRegistered,
          defaultVatRate: companyData.defaultVatRate ?? '',
        };

        // force 0 when not VAT registered
        if (!processedData.isVatRegistered) processedData.defaultVatRate = '0';

        setCompany(processedData);
        setLogoPreview(buildEntityFileUrl(processedData.id, ApiConstants.EntityTypes.Company));
        setLicensePreview(processedData.licenseUrl?.startsWith('/uploads/') ? processedData.licenseUrl : '');
      })
      .catch((err) => {
        console.error(err);
        toast.error(i18n.t('company.errorWhileFetchingTheCompany'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchCurrencies = () => {
    axiosInstance
      .get<CurrencyOption[]>('/api/currencies/list')
      .then((res) => setCurrencies((res.data || []).filter((c) => c.isActive)))
      .catch((err) => {
        console.error(err);
        setCurrencies([]);
      });
  };

  const fetchIntegrationOptions = () => {
    axiosInstance
      .get<CompanyIntegrationSettingOption[]>(ApiConstants.integrationSettings.companyOptions)
      .then((res) => setIntegrationOptions(res.data || []))
      .catch((err) => {
        console.error(err);
        setIntegrationOptions([]);
      });
  };

  useEffect(() => {
    if (show) {
      fetchComp();
      fetchCurrencies();
      fetchIntegrationOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!company) return;

    const { name, value } = e.target;

    if (name === 'currencyId' || name === 'languageId' || name === 'integrationId') {
      setCompany({ ...company, [name]: value ? Number(value) : null });
      return;
    }

    setCompany({ ...company, [name]: value });
  };

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    const compressed = file.type.startsWith('image/') ? await compressImageIfNeeded(file, { maxKB: DEFAULT_IMAGE_MAX_KB }) : file;

    if (compressed.size > maxUploadBytes) {
      toast.warning(i18n.t('company.imageTooLarge', { defaultValue: 'Image must be 200KB or smaller.' }));
      e.target.value = '';
      return;
    }

    setLogoFile(compressed);
    setLogoPreview(URL.createObjectURL(compressed));
    e.target.value = '';
  };

  const handleLicenseChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    const compressed = file.type.startsWith('image/') ? await compressImageIfNeeded(file, { maxKB: DEFAULT_IMAGE_MAX_KB }) : file;

    if (compressed.type.startsWith('image/') && compressed.size > maxUploadBytes) {
      toast.warning(i18n.t('company.imageTooLarge', { defaultValue: 'Image must be 200KB or smaller.' }));
      e.target.value = '';
      return;
    }

    setLicenseFile(compressed);

    if (compressed.type.startsWith('image/')) {
      setLicensePreview(URL.createObjectURL(compressed));
    } else {
      setLicensePreview('');
    }
    e.target.value = '';
  };

  const resolveMenuUrl = () => {
    if (!company) return '';
    const direct = (company.website || '').trim();
    if (direct) return direct;

    const key = (company.menyKeyCode || '').trim();
    if (!key) return '';
    return `http://dev.architech.al/menu/${encodeURIComponent(key)}`;
  };

  const handleDownloadQrTemplate = async () => {
    if (!company) return;

    const menuUrl = resolveMenuUrl();
    if (!menuUrl) {
      toast.warning(i18n.t('company.websiteMissing', { defaultValue: 'Website is required to generate QR template.' }));
      return;
    }

    const v = Date.now();
    const menuUrlWithV = menuUrl.includes('?') ? `${menuUrl}&v=${v}` : `${menuUrl}?v=${v}`;

    const logoUrl = (logoPreview || company.logoUrl || '').trim();
    const logoUrlWithV =
      logoUrl && (logoUrl.startsWith('http') || logoUrl.startsWith('/'))
        ? logoUrl.includes('?')
          ? `${logoUrl}&v=${v}`
          : `${logoUrl}?v=${v}`
        : logoUrl;

    try {
      setIsDownloadingQr(true);
      await downloadCompanyQrTemplate({
        companyName: company.name,
        companyLogoUrl: logoUrlWithV,
        website: menuUrlWithV,
      });
      toast.success(i18n.t('company.qrTemplateDownloaded', { defaultValue: 'QR template downloaded.' }));
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('company.qrTemplateFailed', { defaultValue: 'Failed to generate QR template.' }));
    } finally {
      setIsDownloadingQr(false);
    }
  };

  const handleSave = async () => {
    if (!company) return;
    setIsLoading(true);

    try {
      const formData = new FormData();

      formData.append('id', company.id.toString());
      formData.append('name', company.name);
      formData.append('code', company.code);
      formData.append('locationName', company.locationName);
      if (company.locationId != null) formData.append('locationId', String(company.locationId));
      formData.append('email', company.email);
      formData.append('phone', company.phone);
      formData.append('fax', company.fax);
      formData.append('website', company.website);
      formData.append('workingHours', company.workingHours);
      formData.append('menyKeyCode', company.menyKeyCode);
      formData.append('licenseKey', company.licenseKey);
      formData.append('registrationNumber', company.registrationNumber);

      if (company.currencyId != null) formData.append('currencyId', String(company.currencyId));
      if (company.integrationId != null) formData.append('integrationId', String(company.integrationId));
      if (company.languageId != null) formData.append('languageId', String(company.languageId));

      formData.append('isActive', company.isActive ? 'true' : 'false');
      formData.append('isPublic', company.isPublic ? 'true' : 'false');

      // ✅ MUST be strings (FormData)
      formData.append('isVatRegistered', String(company.isVatRegistered));

      // if not VAT registered -> force 0
      const vatRateToSend = company.isVatRegistered ? (company.defaultVatRate ?? '') : '0';
      formData.append('defaultVatRate', vatRateToSend);

      formData.append('description', company.description);

      if (logoFile) formData.append('logoFile', logoFile);
      if (licenseFile) formData.append('licenseFile', licenseFile);

      await axiosInstance.post('/api/company/update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(i18n.t('company.companySavedSuccessfully!'));
      fetchComp();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('company.errorWhileSavingTheCompany!'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!show || !company) return null;

  return (
    <div className="companyWrap">
      <div className="companyContainer">
        <div className="companyTopbar">
          <h1 className="companyTitle">{i18n.t('company.companyInformation')}</h1>
        </div>

        <div className="companyForm">
          <div className="companyFormBody">
            <div className="companyGrid">
              <div className="companyField">
                <label>{i18n.t('generic.code')}</label>
                <input name="code" value={company.code} onChange={handleChange} disabled={isLoading} placeholder={i18n.t('generic.code')} />
              </div>

              <div className="companyField">
                <label>{i18n.t('company.registrationNumber')}</label>
                <input
                  name="registrationNumber"
                  value={company.registrationNumber}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder={i18n.t('company.registrationNumber')}
                />
              </div>

              <div className="companyField">
                <label>{i18n.t('generic.name')}</label>
                <input
                  name="name"
                  value={company.name}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder={i18n.t('company.enterCompanyName')}
                />
              </div>

              <div className="companyField">
                <label>{i18n.t('company.workingHours')}</label>
                <input
                  name="workingHours"
                  value={company.workingHours}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="e.g., 9:00 AM - 5:00 PM"
                />
              </div>

              <div className="companyField">
                <label>{i18n.t('company.website', { defaultValue: 'Website' })}</label>
                <input name="website" value={company.website} onChange={handleChange} disabled={isLoading} placeholder="https://example.com" />
              </div>

              <div className="companyField">
                <label>{i18n.t('generic.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={company.email}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="company@example.com"
                />
              </div>

              <div className="companyField">
                <label>FAX</label>
                <input name="fax" value={company.fax} onChange={handleChange} disabled={isLoading} placeholder="Fax" />
              </div>

              <div className="companyField">
                <label>{i18n.t('company.phone')}</label>
                <input name="phone" value={company.phone} onChange={handleChange} disabled={isLoading} placeholder={i18n.t('company.phone')} />
              </div>

              <div className="companyField">
                <label>{i18n.t('company.currency', { defaultValue: 'Currency' })}</label>
                <select name="currencyId" value={company.currencyId ?? ''} onChange={handleChange} disabled={isLoading}>
                  <option value="">{i18n.t('company.selectCurrency', { defaultValue: 'Select currency' })}</option>
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code}
                      {currency.symbol ? ` (${currency.symbol})` : ''} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="companyField">
                <label>{i18n.t('company.integrationSetting', { defaultValue: 'Fiscal Integration' })}</label>
                <select name="integrationId" value={company.integrationId ?? ''} onChange={handleChange} disabled={isLoading}>
                  <option value="">{i18n.t('company.selectIntegrationSetting', { defaultValue: 'Select integration' })}</option>
                  {integrationOptions.map((option) => (
                    <option key={option.integrationId} value={option.integrationId}>
                      {option.integrationName} ({option.integrationCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="companyField">
                <label>{i18n.t('company.language', { defaultValue: 'Language' })}</label>
                <select name="languageId" value={company.languageId ?? ''} onChange={handleChange} disabled={isLoading}>
                  <option value="">{i18n.t('company.selectLanguage', { defaultValue: 'Select language' })}</option>
                  {languageOptions.map((language) => (
                    <option key={language.id} value={language.id}>
                      {language.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="companyField">
                <label>{i18n.t('company.location')}</label>
                <div className="location-picker-inline">
                  <input
                    name="locationName"
                    value={company.locationName}
                    onChange={handleChange}
                    disabled={isLoading}
                    placeholder={i18n.t('company.location')}
                  />
                  <button type="button" className="location-picker-trigger" onClick={() => setIsLocationPickerOpen(true)} disabled={isLoading}>
                    {i18n.t('company.pickLocation', { defaultValue: 'Pick location' })}
                  </button>
                </div>
              </div>

              <div className="companyField">
                <label>{i18n.t('company.menyKeyCode', { defaultValue: 'Meny Key Code' })}</label>
                <input
                  name="menyKeyCode"
                  value={company.menyKeyCode}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder={i18n.t('company.menyKeyCode', { defaultValue: 'Meny Key Code' })}
                />
              </div>

              <div className="companyField">
                <label>{i18n.t('company.licenseKey', { defaultValue: 'License Key' })}</label>
                <input
                  name="licenseKey"
                  value={company.licenseKey}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder={i18n.t('company.licenseKey', { defaultValue: 'License Key' })}
                />
              </div>

              <div className="companyField companyCheckbox compact">
                <label>
                  <input
                    type="checkbox"
                    checked={company.isActive}
                    onChange={() => setCompany({ ...company, isActive: !company.isActive })}
                    disabled={isLoading}
                  />
                  {i18n.t('company.activeCompany')}
                </label>
              </div>

              <div className="companyField companyCheckbox compact">
                <label>
                  <input
                    type="checkbox"
                    checked={company.isPublic}
                    onChange={() => setCompany({ ...company, isPublic: !company.isPublic })}
                    disabled={isLoading}
                  />
                  {i18n.t('company.isPublic', { defaultValue: 'Public company' })}
                </label>
              </div>

              <div className="companyField companyCheckbox compact">
                <label>
                  <input
                    type="checkbox"
                    checked={company.isVatRegistered}
                    onChange={() => {
                      const next = !company.isVatRegistered;
                      setCompany({
                        ...company,
                        isVatRegistered: next,
                        defaultVatRate: next ? (company.defaultVatRate && company.defaultVatRate !== '0' ? company.defaultVatRate : '20') : '0',
                      });
                    }}
                    disabled={isLoading}
                  />
                  {i18n.t('company.vatRegistered', { defaultValue: 'Subjekt TVSH' })}
                </label>
              </div>

              <div className="companyField">
                <label>{i18n.t('company.defaultVatRate', { defaultValue: 'Default TVSH (%)' })}</label>
                <input
                  name="defaultVatRate"
                  value={company.defaultVatRate ?? ''}
                  onChange={handleChange}
                  disabled={isLoading || !company.isVatRegistered}
                  placeholder="20"
                />
              </div>

              <div className="companyUploadSection">
                <div className="companyLogo">
                  <label>LOGO</label>
                  <input type="file" onChange={handleLogoChange} accept="image/*" disabled={isLoading} />
                  {logoPreview && <img src={logoPreview} alt={i18n.t('company.logoPreview')} className="companyLogoPreview" />}
                </div>

                <div className="companyLicense">
                  <label>{i18n.t('company.license/Certifikate')}</label>
                  <input type="file" onChange={handleLicenseChange} accept="image/*,.pdf,.doc,.docx,.png,.jpg,.jpeg" disabled={isLoading} />
                  {licensePreview ? (
                    <img src={licensePreview} alt={i18n.t('company.licensePreview')} className="companyLicensePreview" />
                  ) : (
                    licenseFile && <div className="companyFileName">{licenseFile.name}</div>
                  )}
                </div>
              </div>

              <div className="companyField companyFull">
                <label>{i18n.t('company.description')}</label>
                <textarea
                  rows={4}
                  name="description"
                  value={company.description}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder={i18n.t('company.enterCompanyDescription...')}
                />
              </div>

            </div>

            <div className="companyFooter">
              <button type="button" className="companyBtn companyBtnSecondary" onClick={onClose} disabled={isLoading}>
                {i18n.t('tables.close')}
              </button>

              <button
                type="button"
                className="companyBtn companyBtnSecondary"
                onClick={handleDownloadQrTemplate}
                disabled={isLoading || isDownloadingQr}
              >
                {isDownloadingQr
                  ? i18n.t('company.generatingQr', { defaultValue: 'Generating...' })
                  : i18n.t('company.downloadQrTemplate', { defaultValue: 'Download QR Template' })}
              </button>

              <button type="button" className="companyBtn companyBtnPrimary" onClick={handleSave} disabled={isLoading}>
                {isLoading ? i18n.t('company.saving') : i18n.t('generic.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <LocationPickerModal
        open={isLocationPickerOpen}
        title={i18n.t('company.pickLocation', { defaultValue: 'Pick location' })}
        initialQuery={company.locationName}
        onClose={() => setIsLocationPickerOpen(false)}
        onSelect={(value: LocationPickerValue) =>
          setCompany((prev) =>
            prev
              ? {
                  ...prev,
                  locationId: value.locationId ?? null,
                  locationName: value.label,
                  location: {
                    id: value.locationId ?? null,
                    name: value.label,
                    type: value.type ?? null,
                    lat: value.latitude ?? null,
                    lng: value.longitude ?? null,
                  },
                }
              : prev,
          )
        }
      />
    </div>
  );
};

export default CompanyModal;
